import { useCallback, useState } from 'react';
import { TabbedChips } from '@cbhq/cds-web/chips/TabbedChips';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Link, TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { BaseL3Chain } from ':cloud/types/ts_types';
import { BASE_APPCHAINS_DOCS_LINK } from ':cloud/utils/links';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';

import { DetailItem } from './DetailItem';

const INTEGRATE_TABS = [
  {
    id: 'quick-reference',
    label: 'Quick reference',
  },
  {
    id: 'api-urls',
    label: 'API URLs',
  },
  {
    id: 'addressses',
    label: 'Addresses',
  },
];

type BaseAppchainsIntegrateTabProps = {
  chain?: BaseL3Chain;
};

export function BaseAppchainsIntegrateTab({ chain }: BaseAppchainsIntegrateTabProps) {
  const [tab, setTab] = useState(INTEGRATE_TABS[0].id);

  const handleVisitDocsPress = useCallback(() => {
    openUrlInNewWindow(BASE_APPCHAINS_DOCS_LINK);
  }, []);

  useLogViewOnMount(EventName.base_appchains_integrate_tab_view);

  return (
    <Box display="block" height="100%" flexGrow={1} spacing={7}>
      <VStack spacingBottom={10} gap={1}>
        <TextTitle3 as="h3">Integrate your appchain</TextTitle3>
        <TextBody as="p" color="foregroundMuted" spacingBottom={2}>
          Reference your appchain’s properties, API URLs (including RPC), and contract addresses to
          integrate. For more,<Link onPress={handleVisitDocsPress}>visit the docs.</Link>
        </TextBody>
        <TabbedChips value={tab} onChange={setTab} tabs={INTEGRATE_TABS} />
        {tab === INTEGRATE_TABS[0]?.id && (
          <VStack width="100%" maxWidth={700} spacingVertical={7} gap={4}>
            <DetailItem showCopy label="Slug" value={chain?.name} />
            <DetailItem showCopy label="Chain ID" value={chain?.id} />
            <DetailItem
              showCopy={!!chain?.customizations?.customGasToken}
              label="Gas Token"
              value={chain?.customizations?.customGasToken || '--'}
            />
            <DetailItem label="Withdrawal period" value={chain?.customizations?.withdrawPeriod} />
            <DetailItem label="Gas Block Limit" value={chain?.customizations?.blockGasLimit} />
            <DetailItem label="Data Availability" value={chain?.customizations?.dataAvailability} />
          </VStack>
        )}
        {tab === INTEGRATE_TABS[1]?.id && (
          <VStack width="100%" maxWidth={700} spacingVertical={7} gap={4}>
            <DetailItem showCopy label="RPC" value={chain?.resources?.rpcUrl} />
            <DetailItem showCopy label="WebSocket" value={chain?.resources?.wsUrl} />
          </VStack>
        )}
        {tab === INTEGRATE_TABS[2]?.id && (
          <VStack width="100%" maxWidth={700} spacingVertical={7} gap={4}>
            <DetailItem showCopy label="Optimism Portal" value={chain?.addresses?.optimismPortal} />
            <DetailItem
              showCopy
              label="L2 Output Oracle"
              value={chain?.addresses?.l2OutputOracle}
            />
            <DetailItem showCopy label="System Config" value={chain?.addresses?.systemConfig} />
            <DetailItem
              showCopy
              label="L1 Cross Domain"
              value={chain?.addresses?.l1CrossDomainMessenger}
            />
            <DetailItem
              showCopy
              label="L1 Standard Bridge"
              value={chain?.addresses?.l1StandardBridge}
            />
            <DetailItem
              showCopy
              label="L1 ERC721 Bridge"
              value={chain?.addresses?.l1Erc721Bridge}
            />
            <DetailItem
              showCopy
              label="Optimism ERC20 Factory"
              value={chain?.addresses?.optimismMintableErc20Factory}
            />
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
